import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, Filter, X, Eye, Clock, AlertTriangle, CheckCircle2, Loader2, Package, MessageSquare, Ban, Download, Edit3,
} from 'lucide-react';
import OrderDetailDrawer from './OrderDetailDrawer';
import Pagination, { paginate, PAGE_SIZE } from './Pagination';
import CancelOrderModal from './CancelOrderModal';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useUnreadBadges } from '../context/UnreadBadgeContext';
import FilterPanel from './ui-shared/FilterPanel';
import DateRangeFilter from './ui-shared/DateRangeFilter';
import ExportMenu from './ui-shared/ExportMenu';
import SortableHeader, { useTableSort } from './SortableHeader';
import { PriorityCountdown } from './orders/PriorityCountdown';
import { subscribeUnreadRefresh } from '../lib/unreadBus';
import { CustomerBadges, useNewCustomerDays } from './CustomerBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_META = {
  highest: { label: 'Highest', color: 'text-red-700',    bg: 'bg-red-100',    ring: 'ring-red-300' },
  high:    { label: 'High',    color: 'text-orange-700', bg: 'bg-orange-100', ring: 'ring-orange-300' },
  medium:  { label: 'Medium',  color: 'text-blue-700',   bg: 'bg-blue-100',   ring: 'ring-blue-300' },
  low:     { label: 'Low',     color: 'text-emerald-700',bg: 'bg-emerald-100',ring: 'ring-emerald-300' },
  lowest:  { label: 'Lowest',  color: 'text-slate-700',  bg: 'bg-slate-100',  ring: 'ring-slate-300' },
};

const STATUS_META = {
  pending:   { label: 'Pending',   cls: 'bg-slate-200 text-slate-700' },
  open:      { label: 'Open',      cls: 'bg-blue-100 text-blue-700' },
  working:   { label: 'Working',   cls: 'bg-amber-100 text-amber-700' },
  waiting:   { label: 'Waiting',   cls: 'bg-yellow-100 text-yellow-700' },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
  closed:    { label: 'Closed',    cls: 'bg-slate-300 text-slate-800' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-700' },
};

// Format ISO date → dd.mm.yyyy HH:MM
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Remaining time from now -> deadline (negative = overdue). Ticks every second.
// Kept locally because DeadlineCell still uses fmtDate; the live-tick logic
// moved to `components/orders/PriorityCountdown.jsx` so OrderDetailDrawer
// can reuse the exact same countdown without duplicating it.
const useTickingRemaining = (deadline) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!deadline) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadline]);
  if (!deadline) return null;
  const t = new Date(deadline).getTime();
  if (Number.isNaN(t)) return null;
  return t - now;
};

const fmtTimeOnly = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Live HH:MM:SS countdown shown UNDER the priority badge.
// Hides when status is completed/cancelled. Switches to "Will be ready at HH:MM"
// when admin set a custom ready_at AND that time is still in the future.

const DeadlineCell = ({ deadline, readyAt, status }) => {
  const display = readyAt || deadline;
  if (!display) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className={`text-xs font-mono ${status === 'completed' || status === 'cancelled' ? 'text-slate-400' : 'text-slate-700'}`}>
      <div>{fmtDate(display)}</div>
      {readyAt && <div className="text-[10px] text-cyan-600 font-semibold uppercase tracking-wider">Custom</div>}
    </div>
  );
};

export default function OrdersTable({ token, canEditPriority = false, canImport = false, canEditStatus = false, scope = 'admin', onOpenCustomer, initialStatusFilter = '', initialSmartFilter = '', initialEcuFinderOnly = false, initialFilters = null, initialOpenOrderId = '', onOpenedOrderConsumed, onEditOrder }) {
  const { t } = useTranslation('orders');
  const { t: tCommon } = useTranslation('common');
  const newCustomerDays = useNewCustomerDays(scope === 'admin' ? token : null);
  const tNav = (k) => tCommon(`nav.${k}`);
  // Admin UI must stay 100% English (per i18n spec). Use `tt` as a scope-aware
  // translator: returns t(key) for customers and the raw English fallback for admins.
  const isAdmin = scope === 'admin';
  const tt = useCallback((key, fallback) => (isAdmin ? fallback : t(key, { defaultValue: fallback })), [isAdmin, t]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ vehicle_types: [], brands: [], ecu_types: [], companies: [], statuses: [], priorities: [], services: [], countries: [], accounts: [], payment_companies: [] });
  const [filters, setFilters] = useState({
    order_number: '', manufacturer: '', model: '', engine: '', ecu_type: '', vin: '',
    service: '', vehicle_type: '', status: initialStatusFilter || '', priority: '',
    first_name: '', surname: '', company: '', customer_email: '',
    country: '', user_id: '', payment_company: '',
    date_start: '', date_end: '',
    // Smart Vehicle quick-filter: '' = all, 'pending' = not yet promoted,
    // 'promoted' = already promoted to catalog. Seeded from parent so
    // AdminDashboard can deep-link with smart_vehicle=pending.
    smart_vehicle: initialSmartFilter || '',
    // ECU-finder-only filter. Seeded by the parent when the admin
    // clicks the "ECU Finder" KPI tile on the dashboard / analytics
    // page. Mapped to the `ecu_finder_only` query param on the
    // /orders backend route.
    ecu_finder_only: !!initialEcuFinderOnly,
    // Deep-link from Analytics — seeded at mount (not in an effect) so the
    // very first fetch already carries the filter.
    ...(initialFilters
      ? Object.fromEntries(Object.entries(initialFilters).filter(([k]) => k !== '_nonce'))
      : {}),
  });
  const [showFilters, setShowFilters] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [busyRowId, setBusyRowId] = useState(null);
  const [openOrderId, setOpenOrderId] = useState(null);
  const [cancelOrder, setCancelOrder] = useState(null);
  const [unreadByOrder, setUnreadByOrder] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const pageSize = PAGE_SIZE;
  // Server-side sort state — owned by the SortableHeader hook. Changing
  // the sort resets pagination to page 1 (same as filter changes).
  const { sortBy, sortDir, onSortChange, sortParams } = useTableSort({
    onPageReset: () => setPage(1),
  });
  // Granular unread badge — when the user opens a specific order we
  // remove its ID from the menu badge counter.
  const { markOrderSeen, unreadOrderIds } = useUnreadBadges();
  const openOrder = useCallback((id) => {
    setOpenOrderId(id);
    markOrderSeen(id);
  }, [markOrderSeen]);
  // Debounce filter changes so rapid typing (admin hunting for a VIN /
  // manufacturer / customer name) doesn't fire a fetch per keystroke —
  // the old behaviour made the whole UI feel frozen on slower links.
  const debouncedFilters = useDebouncedValue(filters, 400);

  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Ref for the scroll container — vertical scroll only now that the table
  // uses `table-fixed` with explicit colgroup widths (no horizontal scroll).
  const tableScrollRef = useRef(null);
  const tableEl = useRef(null);

  const loadUnread = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/orders/messages/unread-summary`, { headers: authHeaders });
      if (!res.ok) return;
      const data = await res.json();
      setUnreadByOrder(data || {});
    } catch (_) {}
  }, [authHeaders]);

  useEffect(() => {
    loadUnread();
    const id = setInterval(loadUnread, 60000);
    const unsub = subscribeUnreadRefresh(loadUnread);
    return () => { clearInterval(id); unsub(); };
  }, [loadUnread]);

  // When the parent (e.g. UserPanel) navigates here with a new initialStatusFilter,
  // sync it into the table's local filters so the resulting list reflects the click.
  useEffect(() => {
    setFilters((prev) => (prev.status === (initialStatusFilter || '') ? prev : { ...prev, status: initialStatusFilter || '' }));
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStatusFilter]);

  // Same pattern for the Smart Vehicle quick-filter — when AdminDashboard
  // navigates here with `smart_vehicle=pending` we want the dropdown to
  // already reflect that choice on first render.
  useEffect(() => {
    setFilters((prev) => (prev.smart_vehicle === (initialSmartFilter || '') ? prev : { ...prev, smart_vehicle: initialSmartFilter || '' }));
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSmartFilter]);

  // Sync ECU-finder-only filter from parent — fires when admin clicks
  // the "ECU Finder" KPI tile on the Dashboard / Analytics page.
  useEffect(() => {
    setFilters((prev) => (prev.ecu_finder_only === !!initialEcuFinderOnly ? prev : { ...prev, ecu_finder_only: !!initialEcuFinderOnly }));
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEcuFinderOnly]);

  // Rich deep-link from Analytics — an arbitrary subset of filter keys
  // (status / manufacturer / ecu_type / customer_email / date range …).
  // `_nonce` on the incoming object makes a repeated click on the same
  // chart element re-apply the same filter.
  useEffect(() => {
    if (!initialFilters) return;
    const { _nonce, ...incoming } = initialFilters;
    setFilters((prev) => {
      const next = { ...prev };
      Object.keys(prev).forEach((k) => {
        if (k === 'ecu_finder_only') next[k] = false;
        else next[k] = '';
      });
      Object.entries(incoming).forEach(([k, v]) => { if (k in next) next[k] = v; });
      return next;
    });
    setShowFilters(true);
    if (['first_name', 'surname', 'customer_email', 'country', 'user_id', 'payment_company']
      .some((k) => incoming[k])) setAdvancedOpen(true);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters?._nonce]);

  // Cross-component open-order request — when AdminPanel hands us an
  // `initialOpenOrderId` (e.g. user clicked an order link inside a
  // CustomerProfileDrawer transaction row), open that order's drawer
  // immediately and clear the prop on the parent so it doesn't fire again.
  useEffect(() => {
    if (!initialOpenOrderId) return;
    setOpenOrderId(initialOpenOrderId);
    markOrderSeen(initialOpenOrderId);
    if (onOpenedOrderConsumed) onOpenedOrderConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpenOrderId]);

  // Guards against out-of-order responses: a slow unfiltered request must
  // not overwrite the rows of a newer, filtered one (deep-links from
  // Analytics fire both within a few hundred ms).
  const loadSeqRef = useRef(0);
  const loadOrders = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const baseParams = new URLSearchParams();
      Object.entries(debouncedFilters).forEach(([k, v]) => { if (v) baseParams.append(k, v); });
      const pageParams = new URLSearchParams(baseParams);
      pageParams.append('skip', (page - 1) * pageSize);
      pageParams.append('limit', pageSize);
      // Server-side sort — applied AFTER filters, BEFORE pagination.
      // Empty when no column is active so the endpoint's default sort
      // (`created_at desc`) governs.
      if (sortBy) {
        pageParams.append('sort_by', sortBy);
        if (sortDir) pageParams.append('sort_dir', sortDir);
      }
      const [res, cntRes] = await Promise.all([
        fetch(`${API_URL}/api/orders/?${pageParams}`, { headers: authHeaders }),
        fetch(`${API_URL}/api/orders/count?${baseParams}`, { headers: authHeaders }),
      ]);
      const raw = await res.text();
      const data = raw ? JSON.parse(raw) : [];
      if (!res.ok) throw new Error(data?.detail || 'Failed to load');
      if (seq !== loadSeqRef.current) return;   // a newer request is in flight
      setOrders(Array.isArray(data) ? data : []);
      if (cntRes.ok) {
        const c = await cntRes.json();
        if (seq !== loadSeqRef.current) return;
        setTotalCount(c?.total || 0);
        setTotalAmount(Number(c?.total_amount || 0));
      }
    } catch (e) {
      console.error(e);
      if (seq === loadSeqRef.current) setOrders([]);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [debouncedFilters, page, pageSize, authHeaders, sortBy, sortDir]);

  useEffect(() => { loadOrders(); }, [loadOrders]);
  // When a filter changes, return to page 1 so the user doesn't end up on
  // an empty paginated page.
  useEffect(() => { setPage(1); }, [debouncedFilters]);
  // Periodic background refresh so admin-driven status changes (auto-complete
  // on ready_at, manual status updates) propagate without a hard refresh.
  useEffect(() => {
    const id = setInterval(() => loadOrders(), 60000);
    return () => clearInterval(id);
  }, [loadOrders]);

  useEffect(() => {
    // Filter options — both admin & client are now allowed; server scopes
    // the aggregations appropriately.
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/orders/filters/options`, { headers: authHeaders });
        if (!res.ok) return;
        const data = await res.json();
        setFilterOptions(data);
      } catch (_) {}
    })();
  }, [authHeaders]);

  const onFilter = () => { setPage(1); loadOrders(); };
  const onClear = () => {
    setFilters({ order_number: '', manufacturer: '', model: '', engine: '', ecu_type: '', vin: '', service: '', vehicle_type: '', status: '', priority: '', first_name: '', surname: '', company: '', customer_email: '', country: '', user_id: '', payment_company: '', date_start: '', date_end: '', smart_vehicle: '' });
    setPage(1);
  };

  const changePriority = async (order, newPriority) => {
    if (!canEditPriority || order.priority === newPriority) return;
    setBusyRowId(order.id);
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/priority`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority: newPriority }),
      });
      const raw = await res.text();
      const data = raw ? JSON.parse(raw) : null;
      if (!res.ok) throw new Error(data?.detail || 'Update failed');
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, priority: data.priority, priority_deadline: data.priority_deadline } : o)));
    } catch (e) {
      alert('Failed to change priority: ' + e.message);
    } finally {
      setBusyRowId(null);
    }
  };

  const changeStatus = async (order, newStatus) => {
    if (!canEditStatus || order.status === newStatus) return;
    // Status transitions that REFUND the customer (cancelled / closed)
    // are destructive — require explicit confirm + a typed reason so a
    // mis-click in the dropdown can't silently issue a refund. Other
    // status changes proceed instantly as before.
    let reason = '';
    if (newStatus === 'cancelled' || newStatus === 'closed') {
      const total = Number(order.total_price || 0).toFixed(2);
      const verb  = newStatus === 'closed' ? 'Close' : 'Cancel';
      const confirmed = window.confirm(
        `${verb} order ${order.order_number || order.id} and REFUND €${total} to the customer?\n\n` +
        `Status will be set to "${newStatus}" and the full amount returned to the customer's balance.`
      );
      if (!confirmed) return;
      const reasonRaw = window.prompt(
        `Reason for ${verb.toLowerCase()} (sent to the customer by email):`,
        ''
      );
      if (reasonRaw === null) return;     // user clicked Cancel on the prompt
      reason = reasonRaw.trim();
    }
    setBusyRowId(order.id);
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/status`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, ...(reason ? { reason } : {}) }),
      });
      const raw = await res.text();
      const data = raw ? JSON.parse(raw) : null;
      if (!res.ok) throw new Error(data?.detail || 'Update failed');
      loadOrders();
    } catch (e) {
      alert('Failed to change status: ' + e.message);
    } finally {
      setBusyRowId(null);
  }
  };

  // Forward the currently-active filters to the export endpoint so the
  // downloaded XLSX / PDF matches what the operator sees on screen.
  const exportQs = (() => {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== '' && v != null && v !== false) params.set(k, String(v));
    });
    return params.toString();
  })();

  return (
    <div className={`p-4 md:p-6 mx-auto w-full ${scope === 'user' ? 'lg:w-[88%]' : 'lg:w-[88%]'}`} data-testid={`orders-page-${scope}`}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 leading-tight">{tt('title', 'Orders')}</h1>
            <p className="text-xs text-slate-600 leading-snug">{scope === 'user' ? tt('all_your_orders', 'All your orders in one place') : 'All orders across all customers'}</p>
          </div>
        </div>
        {canImport && (
          <ExportMenu
            url={`${API_URL}/api/orders/export${exportQs ? `?${exportQs}` : ''}`}
            token={token}
            filename={`orders-${scope}`}
            testid={`orders-export-${scope}`}
            label={tt('export_orders', 'Export orders')}
          />
        )}
      </div>

      {/* Filters — wrapped in shared FilterPanel for visual consistency.
          Basic: brand/model/engine/ECU/VIN/Order/Vehicle/Service/Status/Priority/Date.
          Advanced (admin only): name/surname/email/country/account/payment company/smart vehicle.
          Active filter chips, Apply/Clear, and all field testids preserved. */}
      {(() => {
        const FILTER_LABELS = {
          order_number: 'Order №', manufacturer: 'Brand', model: 'Model',
          engine: 'Engine', ecu_type: 'ECU', vin: 'VIN', service: 'Service',
          vehicle_type: 'Vehicle', status: 'Status', priority: 'Priority',
          first_name: 'Name', surname: 'Surname', company: 'Company',
          customer_email: 'Email', country: 'Country', user_id: 'Account',
          payment_company: 'Payment co.', date_start: 'From', date_end: 'To',
        };
        const activeChips = Object.entries(filters || {})
          .filter(([, v]) => v !== '' && v != null && v !== false)
          .map(([k, v]) => ({
            key: k, label: FILTER_LABELS[k] || k, value: String(v),
            onRemove: () => setFilters({ ...filters, [k]: '' }),
          }));
        const advancedKeys = ['first_name','surname','customer_email','country','user_id','payment_company'];
        const advancedActiveCount = advancedKeys.filter((k) => filters[k]).length;
        return (
          <FilterPanel
            open={showFilters}
            onToggle={() => setShowFilters((s) => !s)}
            activeChips={activeChips}
            onClearAll={onClear}
            onApply={onFilter}
            advancedOpen={advancedOpen}
            onToggleAdvanced={() => setAdvancedOpen((s) => !s)}
            advancedActiveCount={advancedActiveCount}
            testid="orders-filters"
            labels={{
              filters: tt('panel.filters', 'Filters'),
              hide: tt('panel.hide', 'Hide'),
              show: tt('panel.show', 'Show'),
              clear_all: tt('panel.clear_all', 'Clear all'),
              apply: tt('panel.apply', 'Apply'),
              clear: tt('panel.clear', 'Clear'),
              advanced_filters: tt('panel.advanced_filters', 'Advanced filters'),
              active: tt('panel.active', 'active'),
              hide_filters: tt('panel.hide_filters', 'Hide filters'),
              show_filters: tt('panel.show_filters', 'Show filters'),
            }}
            advancedChildren={scope === 'admin' ? (
              <>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">First name</label>
                  <input type="text" placeholder="First name" value={filters.first_name} onChange={(e) => setFilters({ ...filters, first_name: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-first-name" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Surname</label>
                  <input type="text" placeholder="Surname" value={filters.surname} onChange={(e) => setFilters({ ...filters, surname: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-surname" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Email</label>
                  <input type="text" placeholder="Email" value={filters.customer_email} onChange={(e) => setFilters({ ...filters, customer_email: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-customer-email" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Seller company</label>
                  <select value={filters.payment_company} onChange={(e) => setFilters({ ...filters, payment_company: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-payment-company" title="Filter by the seller company (UK / EU / USA)">
                    <option value="">UK / EU / USA</option>
                    {filterOptions.payment_companies?.map((c) => (<option key={c.id} value={c.id}>{c.label}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Country</label>
                  <select value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-country">
                    <option value="">Any country</option>
                    {filterOptions.countries?.map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Account</label>
                  <AccountPicker
                    value={filters.user_id}
                    onChange={(uid) => setFilters({ ...filters, user_id: uid || '' })}
                    options={filterOptions.accounts || []}
                  />
                </div>
              </>
            ) : null}
          >
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.manufacturer', 'Brand')}</label>
              <input type="text" placeholder={tt('columns.manufacturer', 'Brand')} value={filters.manufacturer} onChange={(e) => setFilters({ ...filters, manufacturer: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-brand" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.model', 'Model')}</label>
              <input type="text" placeholder={tt('columns.model', 'Model')} value={filters.model} onChange={(e) => setFilters({ ...filters, model: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-model" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.engine', 'Engine')}</label>
              <input type="text" placeholder={tt('columns.engine', 'Engine')} value={filters.engine} onChange={(e) => setFilters({ ...filters, engine: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-engine" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">ECU</label>
              <input type="text" placeholder="ECU" value={filters.ecu_type} onChange={(e) => setFilters({ ...filters, ecu_type: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-ecu" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">VIN</label>
              <input type="text" placeholder="VIN" value={filters.vin} onChange={(e) => setFilters({ ...filters, vin: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-vin" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.order', 'Order №')}</label>
              <input type="text" placeholder={tt('columns.order', 'Order №')} value={filters.order_number} onChange={(e) => setFilters({ ...filters, order_number: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-order-number" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('vehicle_type', 'Vehicle type')}</label>
              <select value={filters.vehicle_type} onChange={(e) => setFilters({ ...filters, vehicle_type: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-vehicle-type">
                <option value="">{tt('all_types', 'All types')}</option>
                {filterOptions.vehicle_types?.map((v) => (<option key={v} value={v}>{v}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.service', 'Service')}</label>
              <ServiceTagsFilter
                value={filters.service}
                onChange={(v) => setFilters({ ...filters, service: v })}
                options={filterOptions.services || []}
                isAdmin={isAdmin}
              />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.status', 'Status')}</label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-status">
                <option value="">{tt('filters.all_statuses', 'Any status')}</option>
                {filterOptions.statuses?.map((s) => (<option key={s} value={s}>{tt(`status.${s}`, STATUS_META[s]?.label || s)}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.priority', 'Priority')}</label>
              <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="filter-priority">
                <option value="">{tt('filters.all_priorities', 'Any priority')}</option>
                {filterOptions.priorities?.map((p) => (<option key={p} value={p}>{tt(`priorities.${p}`, PRIORITY_META[p]?.label || p)}</option>))}
              </select>
            </div>
            <DateRangeFilter
              from={filters.date_start}
              to={filters.date_end}
              onChange={({ from, to }) => setFilters({ ...filters, date_start: from, date_end: to })}
              testidPrefix="filter-date"
              label={tt('date_label', 'Date')}
              presetLabels={{
                today: tt('presets.today', 'Today'),
                d7: tt('presets.d7', '7 days'),
                d30: tt('presets.d30', '30 days'),
                this_month: tt('presets.this_month', 'This month'),
              }}
            />
          </FilterPanel>
        );
      })()}

      {/* Table — desktop + tablet. On mobile we render a card stack instead
          (below) so columns don't require horizontal scrolling.
          We render TWO horizontal scrollbars (top + bottom) sharing the
          same content width: long tables stop forcing the admin to
          scroll down to the bottom of the page to scroll right. The
          top scrollbar mirrors the bottom one bidirectionally — a
          `scroll` listener on either pane writes back to the other. */}
      <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
        <div className="max-h-[calc(100vh-260px)] overflow-y-auto overflow-x-hidden" ref={tableScrollRef}>
          <table className="w-full text-sm" ref={tableEl}>
            {/* Content-fit column widths (Feb-2026 redesign):
                - `table-fixed` removed so columns shrink to their content.
                - Narrow columns (Order №, Vehicle, Date, VIN, Amount,
                  Status, Priority, Date end, Options) use
                  `whitespace-nowrap` on each cell, which under
                  `table-auto` collapses the column to MIN content width.
                - Flexible text columns (Service, Manufacturer, Model,
                  Engine, ECU type, Customer) use `truncate` with a
                  `max-w-[…]` so they absorb the remaining width and
                  ellipsise long values — full text in the cell `title`.
                See `tuning-files-redesign-spec.md` companion ТЗ. */}
            <thead className="sticky top-0 z-10 bg-slate-200 border-b border-slate-300">
              <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                <SortableHeader label={tt('columns.order', 'Order №')} field="order_number" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.vehicle', 'Vehicle')} field="vehicle_type" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.service', 'Service')} field="service" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.date', 'Date')} field="date" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.manufacturer', 'Manufacturer')} field="manufacturer" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.model', 'Model')} field="model" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.engine', 'Engine')} field="engine" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.ecu_type', 'ECU type')} field="ecu_type" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <th className="px-2.5 py-3 text-left whitespace-nowrap">VIN</th>
                {scope === 'admin' && <SortableHeader label="Customer" field="customer" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />}
                <SortableHeader label={tt('columns.amount', 'Amount')} field="total_price" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} align="right" className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.status', 'Status')} field="status" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.priority', 'Priority')} field="priority" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.date_end', 'Date end')} field="ready_at" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <th className="px-2.5 py-3 text-center whitespace-nowrap">{tt('columns.options', 'Options')}</th>
              </tr>
            </thead>
            <tbody data-testid="orders-tbody">
              {loading ? (
                <tr><td colSpan={scope === 'admin' ? 15 : 14} className="text-center py-12 text-slate-500"><Loader2 className="w-5 h-5 inline mr-2 animate-spin" />{tt('loading_orders', 'Loading orders…')}</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={scope === 'admin' ? 15 : 14} className="text-center py-12 text-slate-500 text-sm">
                  {tt('empty_filtered', 'No orders match the current filters.')}
                </td></tr>
              ) : (
                orders.map((o, idx) => {
                  const priorityMeta = PRIORITY_META[o.priority] || PRIORITY_META.medium;
                  const statusMeta = STATUS_META[o.status] || STATUS_META.open;
                  const services = (o.service_names && o.service_names.length ? o.service_names : o.selected_options) || [];
                  const hasRowUnread = (unreadByOrder[o.id] || 0) > 0;
                  const isNewOrder = unreadOrderIds && unreadOrderIds.has(o.id);
                  // Row tint priority: red (unread admin) > amber (new
                  // customer) > zebra (white / slate-50). Zebra +
                  // grey-header look restored 08/Jun/2026 to match the
                  // legacy fs.auto-remap.com aesthetic. Hover adds cyan.
                  const zebra = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50';
                  const rowBg = hasRowUnread
                    ? 'bg-red-50/70 border-l-4 border-l-red-500'
                    : isNewOrder
                      ? 'bg-amber-50/60 border-l-4 border-l-amber-400'
                      : zebra;
                  return (
                    <tr key={o.id} className={`border-b border-slate-100 hover:bg-cyan-50/60 transition-colors ${rowBg}`} data-testid={`order-row-${o.id}`}>
                      <td className="px-2.5 py-2.5 font-mono-ui text-[13px] text-cyan-700 font-medium tabular-nums whitespace-nowrap">
                        <button
                          onClick={() => openOrder(o.id)}
                          data-testid={`open-order-${o.id}`}
                          title={
                            o.legacy_file_default_path
                              ? `Legacy file: ${o.legacy_file_default_path}${o.legacy_file_modified_path ? `\nModified: ${o.legacy_file_modified_path}` : ''}`
                              : (o.order_number || '')
                          }
                          className="hover:underline text-cyan-700 font-medium"
                        >
                          {o.order_number || o.id.slice(0, 8)}
                        </button>
                        {isNewOrder && !hasRowUnread && (
                          <span
                            className="ml-2 inline-flex items-center justify-center px-1.5 h-[18px] bg-amber-500 text-white text-[9px] font-extrabold rounded uppercase tracking-wider"
                            title="New since you last visited"
                            data-testid={`new-badge-${o.id}`}
                          >
                            NEW
                          </span>
                        )}
                        {unreadByOrder[o.id] > 0 && (
                          <span
                            className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full animate-pulse"
                            title={`${unreadByOrder[o.id]} new message(s)`}
                            data-testid={`unread-badge-${o.id}`}
                          >
                            {unreadByOrder[o.id]}
                          </span>
                        )}
                        {o.legacy_source === 'old_php_admin' && (
                          <span
                            className="ml-2 inline-flex items-center px-1.5 h-[18px] bg-violet-100 text-violet-700 text-[9px] font-bold rounded uppercase tracking-wider"
                            title={`Legacy imported · legacy_order_id ${o.legacy_order_id || '—'}`}
                            data-testid={`orders-legacy-badge-${o.id}`}
                          >
                            legacy
                          </span>
                        )}
                      </td>
                      <td className="px-2.5 py-2 text-xs whitespace-nowrap">{o.vehicle_type}</td>
                      <td className="px-2.5 py-2 text-xs align-middle max-w-[160px]">
                        {/* Width tuned to fit "Original file purchase" (~22ch = ~155px at
                            text-xs) on a single line. Multi-service orders wrap at the
                            ", " boundaries onto subsequent rows via `whitespace-normal`
                            — much tighter horizontal footprint than the old truncate
                            behaviour which reserved 200 px per cell even for single-
                            service orders. Full CSV still surfaced via the title
                            tooltip on hover. */}
                        <span className="block whitespace-normal break-normal leading-tight" title={services.join(', ')}>{services.join(', ') || '—'}</span>
                      </td>
                      <td className="px-2.5 py-2 text-xs text-slate-600 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                      {/* Content-first sizing: `whitespace-nowrap` lets the browser
                          size each column to the WIDEST value on the current page
                          (thanks to table-layout: auto), so pages with short
                          manufacturer/model/engine names get narrow columns and
                          right-side action columns stay visible. `max-w-*` acts
                          as a safety valve — if an unusually long value shows up
                          it truncates with an ellipsis + title tooltip instead of
                          blowing up the layout. */}
                      <td className="px-2.5 py-2 text-xs font-medium max-w-[130px] truncate" title={o.manufacturer}>{o.manufacturer}</td>
                      <td className="px-2.5 py-2 text-xs max-w-[170px] truncate" title={o.model}>{o.model}</td>
                      <td className="px-2.5 py-2 text-xs max-w-[150px] truncate" title={o.engine}>{o.engine}</td>
                      <td className="px-2.5 py-2 text-xs max-w-[170px] truncate" title={o.ecu_type || ''}>{o.ecu_type || '—'}</td>
                      <td className="px-2.5 py-2 text-[11px] font-mono whitespace-nowrap" style={{ width: '17ch' }}>{o.vin || '—'}</td>
                      {scope === 'admin' && (
                        <td className="px-2.5 py-2 text-xs max-w-[150px]">
                          <div className="flex items-center gap-1 min-w-0">
                            {onOpenCustomer && o.user_id ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onOpenCustomer(o.user_id); }}
                                data-testid={`orders-customer-${o.id}`}
                                title={o.user_email || ''}
                                className="font-medium text-cyan-700 hover:text-cyan-900 hover:underline text-left truncate min-w-0"
                              >
                                {[o.customer_first_name, o.customer_surname].filter(Boolean).join(' ') || o.user_email}
                              </button>
                            ) : (
                              <div className="font-medium truncate min-w-0" title={o.user_email || ''}>{[o.customer_first_name, o.customer_surname].filter(Boolean).join(' ') || o.user_email}</div>
                            )}
                            <CustomerBadges
                              isCompany={o.customer_is_company}
                              companyName={o.customer_company_name || o.customer_company}
                              registrationDate={o.customer_registration_date}
                              newDays={newCustomerDays}
                              testid={`orders-customer-badges-${o.id}`}
                            />
                          </div>
                          {o.customer_company && <div className="text-slate-500 text-[10px] truncate" title={o.customer_company}>{o.customer_company}</div>}
                        </td>
                      )}
                      <td className="px-2.5 py-2 text-right font-bold text-slate-800 whitespace-nowrap tabular-nums">{Number(o.total_price || 0).toFixed(0)}€</td>
                      <td className="px-2.5 py-2 whitespace-nowrap">
                        {canEditStatus ? (
                          <select
                            value={o.status}
                            onChange={(e) => changeStatus(o, e.target.value)}
                            disabled={busyRowId === o.id}
                            data-testid={`status-select-${o.id}`}
                            className={`text-[11px] font-bold px-2 py-1 rounded border-0 focus:ring-2 focus:ring-cyan-500 cursor-pointer ${statusMeta.cls}`}
                          >
                            {Object.entries(STATUS_META).map(([k, m]) => (<option key={k} value={k}>{tt(`status.${k}`, m.label)}</option>))}
                          </select>
                        ) : (
                          <span className={`px-2 py-1 rounded text-[11px] font-bold ${statusMeta.cls} inline-flex items-center gap-1`}>
                            {o.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                            {tt(`status.${o.status}`, statusMeta.label)}
                          </span>
                        )}
                        {/* Quick "Download Modified" button next to a Completed badge.
                            Only shown when the modified file has been uploaded by the
                            engineer; the backend gates the actual download with ready_at. */}
                        {o.status === 'completed' && o.modified_uploaded_at && (
                          <DownloadModifiedButton
                            orderId={o.id}
                            fileName={o.modified_file_name}
                            token={token}
                          />
                        )}
                      </td>
                      <td className="px-2.5 py-2 whitespace-nowrap">
                        {canEditPriority ? (
                          <select
                            value={o.priority}
                            onChange={(e) => changePriority(o, e.target.value)}
                            disabled={busyRowId === o.id}
                            data-testid={`priority-select-${o.id}`}
                            className={`text-[11px] font-bold px-2 py-1 rounded border-0 focus:ring-2 focus:ring-cyan-500 cursor-pointer ${priorityMeta.bg} ${priorityMeta.color}`}
                          >
                            {Object.entries(PRIORITY_META).map(([k, m]) => (<option key={k} value={k}>{tt(`priorities.${k}`, m.label)}</option>))}
                          </select>
                        ) : (
                          <span className={`px-2 py-1 rounded text-[11px] font-bold ${priorityMeta.bg} ${priorityMeta.color} inline-block`}>
                            {tt(`priorities.${o.priority}`, priorityMeta.label)}
                          </span>
                        )}
                        <PriorityCountdown
                          deadline={o.priority_deadline}
                          readyAt={o.ready_at}
                          status={o.status}
                          isAdmin={isAdmin}
                        />
                      </td>
                      <td className="px-2.5 py-2 whitespace-nowrap">
                        <DeadlineCell deadline={o.priority_deadline} readyAt={o.ready_at} status={o.status} />
                      </td>
                      <td className="px-2.5 py-2 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => openOrder(o.id)}
                            data-testid={`view-order-${o.id}`}
                            className="p-1.5 rounded hover:bg-cyan-100 text-cyan-600"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {(() => {
                            // Edit-order button (small red pencil) — sits left of Ticket/Cancel.
                            //   • Admins: always allowed (any status, including completed).
                            //     Closed orders are excluded because they are
                            //     post-refund / archived — editing them would
                            //     undo the refund without the admin realising.
                            //   • Clients: allowed only while the engineer
                            //     hasn't started — i.e. status is still in
                            //     the pre-work bucket AND no ready_at has
                            //     been promised yet. Once admin assigns a
                            //     ready_at or flips to working/waiting/
                            //     completed/closed/cancelled the order is
                            //     locked for the client (the customer can
                            //     always cancel via the Cancel button if
                            //     needed; editing is the wrong tool past
                            //     that point).
                            const isAdmin = scope === 'admin';
                            const preWorkStatuses = ['pending', 'open', 'received'];
                            const editable = isAdmin
                              ? !['closed'].includes(o.status)
                              : preWorkStatuses.includes(o.status) && !o.ready_at;
                            if (!editable || !onEditOrder) return null;
                            return (
                              <button
                                onClick={(e) => { e.stopPropagation(); onEditOrder(o); }}
                                data-testid={`edit-order-${o.id}`}
                                title="Edit order"
                                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                            );
                          })()}
                          {(() => {
                            const unread = unreadByOrder[o.id] || 0;
                            const hasUnread = unread > 0;
                            return (
                              <button
                                onClick={() => openOrder(o.id)}
                                data-testid={`ticket-btn-${o.id}`}
                                title={hasUnread ? `${unread} new message${unread > 1 ? 's' : ''}` : 'Open ticket / chat'}
                                aria-label="Ticket / chat"
                                className={`relative inline-flex items-center justify-center w-7 h-7 rounded-lg border transition-all ${
                                  hasUnread
                                    ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white border-red-600 shadow-md shadow-red-200 animate-pulse hover:from-red-600 hover:to-rose-700'
                                    : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                                }`}
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                                {hasUnread && (
                                  <span
                                    className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 bg-white text-red-600 text-[9px] font-extrabold rounded-full border border-red-600"
                                    data-testid={`ticket-unread-${o.id}`}
                                  >
                                    {unread}
                                  </span>
                                )}
                                {hasUnread && (
                                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-400 ring-2 ring-white animate-ping" />
                                )}
                              </button>
                            );
                          })()}
                          {(() => {
                            // Show Cancel button when:
                            //  - admin: any non-cancelled order
                            //  - user: any non-cancelled, non-completed order (gating happens in modal/backend)
                            const blocked = ['cancelled', 'completed', 'closed'].includes(o.status);
                            if (blocked) return null;
                            return (
                              <button
                                onClick={() => setCancelOrder(o)}
                                data-testid={`cancel-order-${o.id}`}
                                title={canEditPriority ? 'Cancel order (admin)' : tt('cancel_modal.title_after_deadline', 'Cancel order (only after deadline)')}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wide border bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                              >
                                <Ban className="w-3.5 h-3.5" />
                                {tt('cancel_btn', 'Cancel')}
                              </button>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination + total-amount footer.
            The amount sums total_price across ALL orders matching the
            current filter set (not just the visible page) — backend
            does the $sum in an aggregation, no extra round-trip. */}
        {totalCount > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-3 mt-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200" data-testid="orders-total-amount">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                {totalCount === 1 ? tt('order_amount', 'Order amount:') : tt('total_amount_filtered', 'Total amount (filtered):')}
              </span>
              <span className="text-base font-bold text-emerald-700 tabular-nums">
                €{totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <Pagination page={page} total={totalCount} pageSize={pageSize} onChange={setPage} label="orders" isAdmin={scope === 'admin'} />
          </div>
        )}
      </div>

      {/* Mobile card stack — each row becomes a tall card with stacked
          label/value pairs. Matches the pattern the customer is used to
          from their old site. Visible only < md breakpoint. */}
      <div className="md:hidden space-y-3" data-testid="orders-mobile-cards">
        {loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> {tt('loading_orders', 'Loading orders…')}
          </div>
        )}
        {!loading && orders.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            {tt('no_orders_found', 'No orders found.')}
          </div>
        )}
        {!loading && orders.map((o) => {
          const priorityMeta = PRIORITY_META[o.priority] || PRIORITY_META.medium;
          const statusMeta = STATUS_META[o.status] || STATUS_META.open;
          // Customer-side status label translated; admin keeps EN
          const statusLabelTr = tt(`status.${o.status}`, statusMeta.label);
          const services = (o.service_names && o.service_names.length ? o.service_names : o.selected_options) || [];
          const hasUnread = (unreadByOrder[o.id] || 0) > 0;
          const isNewOrder = unreadOrderIds && unreadOrderIds.has(o.id);
          return (
            <div
              key={o.id}
              data-testid={`order-card-${o.id}`}
              className={`bg-white rounded-xl border ${hasUnread ? 'border-red-400 shadow-red-100' : (isNewOrder ? 'border-amber-300' : 'border-slate-200')} shadow-sm overflow-hidden`}
            >
              {/* Card header — order №, status, amount */}
              <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 flex items-center justify-between gap-2">
                <button
                  onClick={() => openOrder(o.id)}
                  data-testid={`open-order-mob-${o.id}`}
                  className="text-left min-w-0 flex-1"
                >
                  <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{tt('columns.order', 'Order №')}</p>
                  <p className="font-mono text-sm font-bold text-cyan-700 truncate">
                    {o.order_number || o.id.slice(0, 8)}
                    {isNewOrder && !hasUnread && (
                      <span className="ml-2 inline-flex items-center justify-center px-1.5 h-[18px] bg-amber-500 text-white text-[9px] font-extrabold rounded uppercase tracking-wider">NEW</span>
                    )}
                    {hasUnread && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full animate-pulse">
                        {unreadByOrder[o.id]}
                      </span>
                    )}
                  </p>
                </button>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{tt('columns.amount', 'Amount')}</p>
                  <p className="text-base font-bold text-slate-800">€{Number(o.total_price || 0).toFixed(0)}</p>
                </div>
              </div>

              {/* Card body — stacked label/value rows */}
              <dl className="px-4 py-3 text-sm divide-y divide-slate-100">
                {scope === 'admin' && (
                  <div className="flex justify-between py-1.5 gap-3">
                    <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Customer</dt>
                    <dd className="text-slate-800 text-right min-w-0 truncate">
                      {onOpenCustomer && o.user_id ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onOpenCustomer(o.user_id); }}
                          className="text-cyan-700 hover:underline font-medium"
                        >
                          {[o.customer_first_name, o.customer_surname].filter(Boolean).join(' ') || o.user_email}
                        </button>
                      ) : ([o.customer_first_name, o.customer_surname].filter(Boolean).join(' ') || o.user_email)}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between py-1.5 gap-3">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">{tt('columns.vehicle', 'Vehicle')}</dt>
                  <dd className="text-slate-800 text-right">{o.vehicle_type}</dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">{tt('columns.manufacturer', 'Brand')}</dt>
                  <dd className="text-slate-800 text-right font-medium">{o.manufacturer}</dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">{tt('columns.model', 'Model')}</dt>
                  <dd className="text-slate-800 text-right">{o.model}</dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">{tt('columns.engine', 'Engine')}</dt>
                  <dd className="text-slate-800 text-right break-words">{o.engine}</dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">{tt('columns.ecu_type', 'ECU type')}</dt>
                  <dd className="text-slate-800 text-right break-words">{o.ecu_type || '—'}</dd>
                </div>
                {o.vin && (
                  <div className="flex justify-between py-1.5 gap-3">
                    <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">VIN</dt>
                    <dd className="text-slate-800 text-right font-mono text-[11px]">{o.vin}</dd>
                  </div>
                )}
                {services.length > 0 && (
                  <div className="py-1.5">
                    <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1">{tt('columns.service', 'Services')}</dt>
                    <dd className="text-slate-800 text-[13px] leading-tight">{services.join(', ')}</dd>
                  </div>
                )}
                <div className="flex justify-between py-1.5 gap-3">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">{tt('columns.date', 'Date')}</dt>
                  <dd className="text-slate-700 text-right text-xs">{fmtDate(o.created_at)}</dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">{tt('columns.status', 'Status')}</dt>
                  <dd>
                    <span className={`px-2 py-1 rounded text-[11px] font-bold ${statusMeta.cls} inline-flex items-center gap-1`}>
                      {o.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                      {statusLabelTr}
                    </span>
                  </dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">{tt('columns.priority', 'Priority')}</dt>
                  <dd className="text-right">
                    {canEditPriority ? (
                      <select
                        value={o.priority}
                        onChange={(e) => changePriority(o, e.target.value)}
                        disabled={busyRowId === o.id}
                        data-testid={`priority-select-mob-${o.id}`}
                        className={`text-[11px] font-bold px-2 py-1 rounded border-0 focus:ring-2 focus:ring-cyan-500 cursor-pointer ${priorityMeta.bg} ${priorityMeta.color}`}
                      >
                        {Object.entries(PRIORITY_META).map(([k, m]) => (<option key={k} value={k}>{tt(`priorities.${k}`, m.label)}</option>))}
                      </select>
                    ) : (
                      <span className={`px-2 py-1 rounded text-[11px] font-bold ${priorityMeta.bg} ${priorityMeta.color} inline-block`}>
                        {tt(`priorities.${o.priority}`, priorityMeta.label)}
                      </span>
                    )}
                    <PriorityCountdown deadline={o.priority_deadline} readyAt={o.ready_at} status={o.status} isAdmin={isAdmin} />
                  </dd>
                </div>
              </dl>

              {/* Card footer — actions */}
              <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center gap-2">
                <button
                  onClick={() => openOrder(o.id)}
                  data-testid={`view-order-mob-${o.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-cyan-50 text-cyan-700 hover:bg-cyan-100 transition-all"
                >
                  <Eye className="w-3.5 h-3.5" /> {tt('details', 'Details')}
                </button>
                <button
                  onClick={() => openOrder(o.id)}
                  data-testid={`ticket-mob-${o.id}`}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${hasUnread ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white animate-pulse' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  {tt('ticket', 'Ticket')}
                  {hasUnread && (
                    <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-white text-red-600 text-[10px] font-extrabold rounded-full">
                      {unreadByOrder[o.id]}
                    </span>
                  )}
                </button>
                {o.status === 'completed' && o.modified_uploaded_at && (
                  <DownloadModifiedButton orderId={o.id} fileName={o.modified_file_name} token={token} />
                )}
                {!['cancelled', 'completed', 'closed'].includes(o.status) && (
                  <button
                    onClick={() => setCancelOrder(o)}
                    data-testid={`cancel-order-mob-${o.id}`}
                    className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-bold bg-rose-50 text-rose-700 hover:bg-rose-100"
                    title={tt('cancel_order_short', 'Cancel order')}
                  >
                    <Ban className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {totalCount > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <Pagination page={page} total={totalCount} pageSize={pageSize} onChange={setPage} label="orders" isAdmin={scope === 'admin'} />
          </div>
        )}
      </div>

      {/* Order detail drawer */}
      {openOrderId && (
        <OrderDetailDrawer
          // Fall back to a minimal stub `{ id }` when the order isn't in the
          // current page — the drawer will auto-fetch the full record from
          // the API. Lets us deep-link from anywhere (e.g. transactions tab).
          order={orders.find((o) => o.id === openOrderId) || { id: openOrderId }}
          token={token}
          isAdmin={scope === 'admin'}
          onClose={() => { setOpenOrderId(null); loadUnread(); }}
          onChanged={() => { loadOrders(); loadUnread(); }}
          onEditOrder={onEditOrder}
        />
      )}

      {cancelOrder && (
        <CancelOrderModal
          order={cancelOrder}
          token={token}
          isAdmin={scope === 'admin'}
          onClose={() => setCancelOrder(null)}
          onCancelled={() => { loadOrders(); loadUnread(); }}
        />
      )}
    </div>
  );
}


/** Account (customer) picker — native <select> with all registered users
 * by full name. When admin picks one, OrdersTable filters to that user's
 * orders only. Combined with Country filter → AND logic (both narrow).
 */
function AccountPicker({ value, onChange, options }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid="filter-account"
      title="Filter by specific customer account"
      className="px-2.5 py-1.5 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
    >
      <option value="">Choose account</option>
      {(options || []).map((a) => (
        <option key={a.user_id} value={a.user_id}>
          {a.label}{a.email ? ` · ${a.email}` : ''}
        </option>
      ))}
    </select>
  );
}

/** Multi-tag Service filter — value is the comma-separated CSV string
 * the OrdersTable stores in `filters.service` (also what the backend API
 * expects). Enter or comma commits a tag; backspace on empty input removes
 * the last tag. Datalist provides autocomplete.  */
function ServiceTagsFilter({ value, onChange, options, isAdmin = false }) {
  const { t } = useTranslation('orders');
  const [draft, setDraft] = useState('');
  const tags = (value || '').split(',').map((s) => s.trim()).filter(Boolean);
  const commit = (txt) => {
    const clean = (txt || '').trim().replace(/,+$/, '');
    if (!clean) return;
    if (tags.includes(clean)) { setDraft(''); return; }
    onChange([...tags, clean].join(','));
    setDraft('');
  };
  const remove = (t) => onChange(tags.filter((x) => x !== t).join(','));
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(draft); }
    else if (e.key === 'Backspace' && !draft && tags.length) {
      e.preventDefault(); onChange(tags.slice(0, -1).join(','));
    }
  };
  return (
    <div
      className="flex flex-wrap items-center gap-1 px-2 py-1 border border-slate-300 rounded-lg text-sm bg-white focus-within:ring-2 focus-within:ring-cyan-500"
      title="Type a service name and press Enter (or comma) to add it. Multiple tags are combined with AND."
    >
      {tags.map((t) => (
        <span key={t} data-testid={`filter-service-tag-${t}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-100 text-cyan-800 rounded text-xs font-medium">
          {t}
          <button type="button" onClick={() => remove(t)} className="hover:text-red-600 font-bold leading-none">×</button>
        </span>
      ))}
      <input
        type="text"
        list="filter-services-list"
        placeholder={
          isAdmin
            ? (tags.length ? 'Add more… (Enter)' : 'Service — Enter to add')
            : (tags.length ? t('add_more_enter', { defaultValue: 'Add more… (Enter)' }) : t('service_enter_to_add', { defaultValue: 'Service — Enter to add' }))
        }
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={() => draft && commit(draft)}
        data-testid="filter-service"
        title="Press Enter or comma to add a service tag. Multiple tags are combined with AND."
        className="flex-1 min-w-[100px] px-1 py-1 outline-none text-sm bg-transparent"
      />
      <datalist id="filter-services-list">
        {(options || []).map((s) => (<option key={s} value={s} />))}
      </datalist>
    </div>
  );
}

/** Download-Modified inline icon button. Streams the modified file via XHR.
 * Backend (/api/orders/{id}/file/modified) enforces ready_at gating.  */
function DownloadModifiedButton({ orderId, fileName, token }) {
  const [busy, setBusy] = useState(false);
  const onClick = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/orders/${orderId}/file/modified`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const t = await r.text();
        alert(`Download failed: ${t || `HTTP ${r.status}`}`);
        return;
      }
      const blob = await r.blob();
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = fileName || `order-${orderId.slice(0, 8)}-modified.bin`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Download error: ${err.message}`);
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={`download-modified-${orderId}`}
      title="Download the modified file"
      className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold uppercase hover:bg-emerald-100 disabled:opacity-50"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
      File
    </button>
  );
}


